import { startCase, upperCase } from "lodash";
import moment from "moment";
import { BASE_URL, getAgeCalculate, getSkinValue } from "./constants";
import jsPDF from "jspdf";
import { client, Reading } from "@src/redux/index";
import toast from "react-hot-toast";
import { HEADER_LOGO } from "./imagesConstants";
import gql from "graphql-tag";
import axios from "axios";
import { Buffer } from "buffer";

export const imageUrlToBase64 = async (url: string): Promise<string> => {
  try {
    const response = await axios.get(url, { responseType: "arraybuffer" });
    const base64 = Buffer.from(response.data, "binary").toString("base64");
    return `data:image/jpeg;base64,${base64}`;
  } catch (error) {
    console.error("Error:", error);
    return "";
  }
};

export const convertImageUrlToBase64 = async (
imageUrl: string,
header: any)
: Promise<string | null> => {
  if (!imageUrl) {
    console.warn("Image URL is empty");
    return null;
  }

  try {
    const response = await fetch(imageUrl, header);

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        if (reader.result) {
          resolve(reader.result.toString());
        } else {
          reject(new Error("Failed to read the file as Base64"));
        }
      };

      reader.onerror = () =>
      reject(new Error("Error converting image to Base64"));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error during image fetch or conversion:", error);
    return null;
  }
};

export const convertEcgUrl = async (
imageUrl: string)
: Promise<string | null> => {
  try {
    const response = await axios.get(
      "https://cors-anywhere.herokuapp.com/" + imageUrl,
      {
        responseType: "arraybuffer"
      }
    );

    const base64 = Buffer.from(response.data, "binary").toString("base64");
    return `data:image/png;base64,${base64}`;
  } catch (error) {
    return "Error fetching image";
  }
};

/**
 * V3's own REST endpoint for URL -> base64 conversion.
 *
 * Derived from REACT_APP_BASE_URL (which ends in /graphql) rather than hardcoded,
 * so dev and production each resolve to their own API automatically. This replaced
 * a hardcoded absolute URL to the legacy V1/V2 REST API, which cannot read V3-native
 * assets — PDF export silently lost logos and ECG images for migrated clients.
 *
 * The "/rest" prefix comes from createRouter(..., "/rest") in api/src/api/serverless.ts.
 */
export const GET_BASE64_FROM_URL_ENDPOINT = BASE_URL.replace(
  /\/graphql\/?$/,
  ""
) + "/rest/get_base64_from_url";

export const getBase64FromUrl = async (imageUrl: string) => {
  try {
    const data = {
      url: imageUrl
    };
    const response = await axios.post(
      GET_BASE64_FROM_URL_ENDPOINT,
      data,
      {
        headers: {
          "Content-Type": "application/json"
        }
      }
    );
    return response?.data;
  } catch (error) {
    throw error;
  }
};

export async function export_report_to_PDF(
_report: Reading,
RootOrganizationName: string,
RootOrganizationShortName: string,
RootOrganizationLogoUrl: string,
HasCovidScore: string,
OrganizationType: string,
ecgUrl?: string)
{
  toast.success("Please wait while we are generating the report for you");
  let report = _report;
  let VincenseLogo = null;
  try {
    VincenseLogo = HEADER_LOGO;
  } catch (ex) {}
  let OrganizationLogo: any = RootOrganizationLogoUrl || null;
  let age = getAgeCalculate(report?.subject?.dob?.toString() || "");
  let lastLevel = "";
  if (report?.subject?.organization) {
    lastLevel = `@ ${report?.subject?.organization.name} ${report?.subject?.organization.level?.name}`;
  }

  const pdf = new jsPDF("p", "px", "a4", true);
  var y = 0;
  var hasPreviouspage = false;
  if (
  report?.vincenseReading?.pulseValue ||
  report?.bpReading?.systolicValue ||
  report?.glucometerReading?.fastingValue ||
  report?.glucometerReading?.ppValue ||
  report?.glucometerReading?.randomValue ||
  report?.weighingReading?.weightValue ||
  report?.weighingReading?.heightValue ||
  report?.weighingReading?.bmiValue)
  {
    hasPreviouspage = true;
    pdf.setFont("courier");
    pdf.setFontSize(18);
    pdf.text(
      RootOrganizationName.length < 20 ?
      RootOrganizationName + " Health " + OrganizationType + " Report" :
      RootOrganizationShortName + " Health " + OrganizationType + " Report",
      230,
      20,
      { align: "center" }
    );
    pdf.setFont("courier");
    pdf.addFont("", "Roboto", "bold", "500");
    pdf.setFontSize(13);
    pdf.text("NAME:", 20, 80);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(report?.subject?.name || "", 70, 80);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text("REF:", 190, 80);
    pdf.addFont("", "Roboto", "normal", "500");
    // pdf.text(report?.subject?.updateReferenceId || "", 220, 80);
    pdf.text(report?.subject?.keyValueList?.[0]?.value || "", 220, 80);
    pdf.addFont("", "Roboto", "bold", "500");
    pdf.text("GENDER:", 20, 100);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(startCase(report?.subject?.gender || ""), 70, 100);
    pdf.addFont("", "Roboto", "bold", "500");
    pdf.text("AGE:", 190, 100);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(age, 220, 100);
    pdf.addFont("", "Roboto", "bold", "500");
    pdf.text("DATE:", 20, 120);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(
      report.datetime ?
      moment(report.datetime).format("DD-MMM-yyyy hh:mmA") :
      "",
      70,
      120
    );
    pdf.text(lastLevel, 190, 120);

    if (OrganizationLogo) {
      pdf.addImage(
        OrganizationLogo,
        upperCase(RootOrganizationLogoUrl.split(".")?.pop()),
        300,
        60,
        100,
        50
      );
    }
    pdf.setDrawColor(128, 128, 128);
    pdf.setLineWidth(0.005);
    pdf.line(20, 130, 430, 130);
    pdf.addFont("", "Roboto", "bold", "500");
    pdf.text("TEST NAME", 20, 145);
    pdf.text("RESULT", 220, 145);
    pdf.text("REFERENCE RANGE", 320, 145);
    pdf.setLineWidth(0.005);
    pdf.line(20, 160, 430, 160);
    y = 160;

    y = y + 30;
    const addRow = (label, result, reference) => {
      pdf.setFont("courier", "bold");
      pdf.text(label, 20, y);
      pdf.setFont("courier", "normal");
      pdf.text(result ? String(result) : "-", 220, y);
      pdf.text(reference ? String(reference) : "-", 320, y);
      y += 30;
    };

    addRow(
      "PULSE",
      report?.vincenseReading?.pulseValue,
      report?.vincenseReading?.pulseReferenceRange
    );
    addRow(
      "SKIN TEMPERATURE",
      getSkinValue(report?.vincenseReading?.skinValue, true, "-"),
      "-"
    );
    addRow(
      "SP02",
      report?.vincenseReading?.o2Value,
      report?.vincenseReading?.o2ReferenceRange
    );
    addRow(
      "RESPIRATORY RATE",
      report?.vincenseReading?.rrValue,
      report?.vincenseReading?.rrReferenceRange
    );
    addRow(
      "SYSTOLIC",
      report?.bpReading?.systolicValue,
      report?.bpReading?.systolicReferenceRange
    );
    addRow(
      "DIASTOLIC",
      report?.bpReading?.diastolicValue,
      report?.bpReading?.diastolicReferenceRange
    );

    if (OrganizationType === "monitoring") {
      addRow(
        "MEAN",
        report?.bpReading?.meanValue,
        report?.bpReading?.meanReferenceRange
      );
    }

    if (
    report?.glucometerReading?.fastingValue ||
    report?.glucometerReading?.ppValue)
    {
      addRow(
        "FASTING SUGAR",
        report?.glucometerReading?.fastingValue,
        report?.glucometerReading?.fastingReferenceRange
      );
      addRow(
        "POST PRANDIAL SUGAR",
        report?.glucometerReading?.ppValue,
        report?.glucometerReading?.ppReferenceRange
      );
    } else {
      addRow(
        "SUGAR",
        report?.glucometerReading?.randomValue,
        report?.glucometerReading?.randomReferenceRange
      );
    }

    if (OrganizationType === "screening") {
      addRow("WEIGHT", report?.weighingReading?.weightValue, "-");
      addRow("HEIGHT", report?.weighingReading?.heightValue, "-");
      addRow("BMI", report?.weighingReading?.bmiValue, "-");
      addRow("ISH SCORE", report?.ishReading?.score, "-");
    }

    pdf.line(20, 590, 430, 590);
    pdf.setFont("courier", "normal");
    pdf.text("Powered by MedIoTek Health Systems Pvt Ltd", 20, 620);
    if (VincenseLogo) pdf.addImage(VincenseLogo, "PNG", 320, 600, 100, 25);
  }

  if (report?.customTestList && report?.customTestList?.length > 0) {
    if (hasPreviouspage) {
      pdf.addPage();
    }
    hasPreviouspage = true;
    pdf.setFont("courier");
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.setFontSize(18);
    pdf.text(
      RootOrganizationName.length < 20 ?
      RootOrganizationName + " Health " + OrganizationType + " Report" :
      RootOrganizationShortName + " Health " + OrganizationType + " Report",
      230,
      20,
      { align: "center" }
    );
    pdf.setFont("courier");
    pdf.addFont("", "Roboto", "bold", "500");
    pdf.setFontSize(13);
    pdf.text("NAME:", 20, 80);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(report?.subject?.name || "-", 70, 80);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text(
      `${
      report?.subject?.keyValueList?.[0]?.organizationSubjectKey?.name ||
      "ID"}: `,

      190,
      80
    );
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(report?.subject?.keyValueList?.[0]?.value || "", 270, 80);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text("GENDER:", 20, 100);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(startCase(report?.subject?.gender), 70, 100);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text("AGE:", 190, 100);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(age, 220, 100);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text("DATE:", 20, 120);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(
      report?.datetime ?
      moment(report.datetime).format("DD-MMM-yyyy hh:mmA") :
      "",
      70,
      120
    );
    pdf.text(lastLevel, 190, 120);

    if (OrganizationLogo) {
      pdf.addImage(
        OrganizationLogo,
        upperCase(RootOrganizationLogoUrl.split(".")?.pop()),
        300,
        60,
        100,
        50
      );
    }
    pdf.setDrawColor(128, 128, 128);
    pdf.setLineWidth(0.005);
    pdf.line(20, 130, 430, 130);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.setFontSize(14);
    pdf.text("OTHER TESTS", 20, 145);
    pdf.setLineWidth(0.005);
    pdf.line(20, 160, 430, 160);

    let yOffset = 180;
    const pageHeight = pdf.internal.pageSize.height;
    const bottomMargin = 40;
    const maxWidth = 390;
    const lineHeight = 14;

    const groupedTests = {};
    report.customTestList.forEach((test) => {
      const groupName =
      test?.customTest?.customTestGroup?.name || "General Tests";
      if (!groupedTests[groupName]) groupedTests[groupName] = [];
      groupedTests[groupName].push(test);
    });

    // ✅ Loop through each group
    Object.keys(groupedTests).forEach((groupName) => {
      if (yOffset > pageHeight - bottomMargin) {
        pdf.addPage();
        yOffset = 60;
      }

      // ✅ Subtitle Header (Bold & Larger Font)
      pdf.setFont("courier", "bold"); // font style
      pdf.setFontSize(14);
      pdf.text(groupName, 20, yOffset);
      yOffset += 15;

      // ✅ Loop through questions with serial numbers
      groupedTests[groupName].forEach((question, index) => {
        if (yOffset > pageHeight - bottomMargin) {
          pdf.addPage();
          yOffset = 60;
        }

        // Serial number prefix (e.g., "1.", "2.", ...)
        const serialNo = `${index + 1}. `;
        const questionText = `${serialNo}${question?.customTest?.name || ""}`;
        const questionLines = pdf.splitTextToSize(questionText, maxWidth);
        const questionHeight = questionLines.length * lineHeight;

        let answerHeight = lineHeight;
        if (question?.customTest?.type === "file" && question.value)
        answerHeight = 70;else
        if (question.value)
        answerHeight =
        pdf.splitTextToSize(String(question.value), maxWidth - 20).length *
        lineHeight;

        if (
        yOffset + questionHeight + answerHeight >
        pageHeight - bottomMargin)
        {
          pdf.addPage();
          yOffset = 60;
        }

        // ✅ Question text in bold
        pdf.setFont("courier", "bold");
        pdf.setFontSize(12);
        questionLines.forEach((line) => {
          pdf.text(line, 25, yOffset);
          yOffset += lineHeight;
        });

        yOffset += 3;

        // ✅ If question type = file, show image
        if (question?.customTest?.type === "file" && question.value) {
          try {
            pdf.addImage(question.value, "JPEG", 40, yOffset, 100, 60);
            yOffset += 70;
          } catch {
            yOffset += 10;
          }
        }
        // ✅ Else print text value normally
        else if (question.value) {
          pdf.setFont("courier", "normal");
          const answerLines = pdf.splitTextToSize(
            String(question.value),
            maxWidth - 20
          );
          answerLines.forEach((line) => {
            if (yOffset > pageHeight - bottomMargin) {
              pdf.addPage();
              yOffset = 60;
            }
            pdf.text(line, 40, yOffset);
            yOffset += lineHeight;
          });
        }

        yOffset += 8;
      });

      yOffset += 10; // Space after group
    });

    pdf.setFontSize(14);
    pdf.setDrawColor(128, 128, 128);
    pdf.setLineWidth(0.005);
    pdf.line(20, pageHeight - 50, 430, pageHeight - 50);
    pdf.setTextColor(0, 0, 0);
    pdf.text("Powered by MedIoTek Health Systems Pvt Ltd", 20, pageHeight - 20);
    if (VincenseLogo) {
      pdf.addImage(VincenseLogo, "PNG", 320, pageHeight - 40, 100, 25);
    }
  }

  if (report?.ecgReading?.ecgUrl) {
    if (hasPreviouspage) {
      pdf.addPage();
    }
    hasPreviouspage = true;
    pdf.setFont("courier");
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.setFontSize(18);
    pdf.text(
      RootOrganizationName.length < 20 ?
      RootOrganizationName + " Health " + OrganizationType + " Report" :
      RootOrganizationShortName + " Health " + OrganizationType + " Report",
      230,
      20,
      { align: "center" }
    );
    pdf.setFont("courier");
    pdf.addFont("", "Roboto", "bold", "500");
    pdf.setFontSize(13);
    pdf.text("NAME:", 20, 80);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(report?.subject?.name || "", 70, 80);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text("REF:", 190, 80);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(report?.subject?.updateReferenceId || "", 220, 80);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text("GENDER:", 20, 100);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(startCase(report?.subject?.gender || ""), 70, 100);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text("AGE:", 190, 100);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(age, 220, 100);
    pdf.addFont("", "Roboto", "bold", "500");

    pdf.text("DATE:", 20, 120);
    pdf.addFont("", "Roboto", "normal", "500");
    pdf.text(
      report.datetime ?
      moment(report.datetime).format("DD-MMM-yyyy hh:mmA") :
      "",
      70,
      120
    );
    pdf.text(lastLevel, 190, 120);

    if (OrganizationLogo) {
      pdf.addImage(
        OrganizationLogo,
        upperCase(RootOrganizationLogoUrl.split(".")?.pop()),
        300,
        60,
        100,
        50
      );
    }
    pdf.setDrawColor(128, 128, 128);
    pdf.setLineWidth(0.005);
    pdf.line(20, 130, 430, 130);

    try {

      const image = await pdfBase64ToImage(ecgUrl || "");
      const imgProps = pdf.getImageProperties(image || "");
      const ecgWidth = pdf.internal.pageSize.getWidth() - 40;
      const ecgHeight = imgProps.height * ecgWidth / imgProps.width;
      const ecgMarginTop = (575 - 150 - ecgHeight) / 2;

      pdf.addImage(
        image || "",
        "PNG",
        20,
        150 + ecgMarginTop,
        ecgWidth,
        ecgHeight
      );
    } catch (error: any) {
      console.error("Error:", error.message);
    }

    pdf.setFontSize(14);
    pdf.setDrawColor(128, 128, 128);
    pdf.setLineWidth(0.005);
    pdf.line(20, 590, 430, 590);
    pdf.setTextColor(0, 0, 0);
    pdf.text("Powered by MedIoTek Health Systems Pvt Ltd", 20, 620);
    if (VincenseLogo) {
      pdf.addImage(VincenseLogo, "PNG", 320, 600, 100, 25);
    }
  }

  pdf.save(
    "VinCense Health " +
    OrganizationType +
    " Report-" +
    moment(report.datetime).format("DD-MMM-yyyy hh:mmA") +
    ".pdf"
  );
}

import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc =
"https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

export async function pdfBase64ToImage(base64Pdf: string) {
  const raw = atob(base64Pdf.split(",")[1]);

  const pdf = await pdfjsLib.getDocument({ data: raw }).promise;
  const page = await pdf.getPage(1);

  const viewport = page.getViewport({ scale: 2 });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: ctx, viewport }).promise;

  return canvas.toDataURL("image/png");
}